import React, { useEffect, useMemo, useState } from 'react';
import './wizard-page.scss';
import CustomButton from '../../shared/custom-button/custom-button';
import { WizardData, WizardProps } from './wizard.model';
import { EventType } from '../../../dtos/analytics.dto';

const Wizard: React.FC<WizardProps> = ({ steps, initialStep = 0, initialData = {}, onComplete, sendEvent, isDisableNext }) => {
    const [currentStep, setCurrentStep] = useState(initialStep);
    const [wizardData, setWizardData] = useState<WizardData>(initialData);
    const [isLoading, setIsLoading] = useState(false);

    const validationResult = useMemo(() =>
        steps[currentStep]?.validate?.(wizardData) ?? { isValid: true }
    ,[currentStep, wizardData, steps]);

    const isCurrentStepValid = validationResult.isValid;
    const errorMessage = validationResult.error;

    useEffect(() => {
        sendEvent(EventType.PageView, { pageName: steps[currentStep].eventName });
    }, [currentStep, steps]);


    const handleNext = async () => {
        if (currentStep < steps.length - 1) {
            sendEvent(EventType.LinkClicked, { button: 'next', location: steps[currentStep].eventName });
            setCurrentStep((prevStep) => prevStep + 1);
        } else {
            setIsLoading(true);
            try {
                await onComplete(wizardData);
            }
            finally {
                setIsLoading(false);
            }
        }
    };

    const handleBack = () => {
        sendEvent(EventType.LinkClicked, { button: 'back', location: steps[currentStep].eventName });
        setCurrentStep((prevStep) => prevStep - 1);
    };

    const handleSkip = async () => {
        sendEvent(EventType.LinkClicked, {
            button: 'skip',
            location: steps[currentStep].eventName,
            [steps[currentStep].eventProperty || steps[currentStep].fieldName]: 'skip'
        });

        const updatedData = {
            ...wizardData,
            [steps[currentStep].fieldName]: undefined,
        };

        setWizardData(updatedData);

        if (currentStep === 0 || currentStep === steps.length - 1) {
            setIsLoading(true);
            try {
                await onComplete(updatedData);
            } finally {
                setIsLoading(false);
            }
        } else {
            setCurrentStep((prevStep) => prevStep + 1);
        }
    };

    const handleDataChange = (data: Partial<WizardData>) => {
        if (currentStep === 0) {
            // If the user is on the first step, reset the wizard data to its initial state
            setWizardData(data);
        } else {
            // Otherwise, update the wizard data as usual
            setWizardData((prevData) => ({ ...prevData, ...data }));
        }
    };

    if (!steps[currentStep]) {
        return null;
    }

    const CurrentStepComponent = steps[currentStep].component;
    const currentStepProps = steps[currentStep].extraProps;

    const isDisableBack = currentStep === 1 && initialStep === 1 && !!initialData.connection || isLoading;

    return (
        <div className="wizard">
            <div className="step-indicator">
                {steps.map((_, index) => {
                    let stepClass = '';
                    if (index < currentStep) {
                        stepClass = 'completed';
                    } else if (index === currentStep) {
                        stepClass = 'active';
                    }
                    
                    return <div
                        key={_.fieldName}
                        className={`step-number ${stepClass}`}>
                        {index + 1}
                    </div>;
                })}
            </div>
            <CurrentStepComponent
                data={wizardData}
                onDataChange={handleDataChange}
                extraProps={currentStepProps}
                errorMessage={errorMessage}
            />
            <div className="wizard-navigation">
                {currentStep > 0 && initialStep !== 2 && (
                    <CustomButton className="btn-back secondary-btn" label={'Back'} onClick={handleBack} isDisabled={isDisableBack}/>
                )}
                <div className="wizard-right-actions">
                    {currentStep !== 1 && (
                        <CustomButton className="btn-skip secondary-btn" label={'Skip'} onClick={handleSkip} isDisabled={isLoading}/>
                    )}
                    <CustomButton className={`btn-next primary-btn${isLoading ? ' loading' : ''}`}
                                  label={steps[currentStep].nextLabel || 'Next'}
                                  onClick={handleNext} isDisabled={!isCurrentStepValid || isDisableNext || isLoading} />
                </div>
            </div>
        </div>
    );
};

export default Wizard;